<template>
  <el-dialog
    id="emergentGoalsModal"
    top="5vh"
    :visible.sync="localVisible"
    width="720px"
    :close-on-click-modal="false"
    :append-to-body="true"
    :show-close="false"
    custom-class="emergent-goals-dialog"
    @close="handleClose"
  >
    <!-- Header -->
    <div slot="title" class="dialog-header">
      <div class="header-left">
        <div class="header-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
          </svg>
        </div>
        <div class="header-text">
          <h2 class="header-title">Discover Goals</h2>
          <p class="header-subtitle">AI-powered suggestions from unmatched work</p>
        </div>
      </div>
      <div class="header-actions">
        <button
          v-if="!loading && hasSuggestions"
          class="refresh-btn"
          @click="handleRefresh"
          :disabled="loading"
          title="Run fresh AI analysis"
        >
          <i class="el-icon-refresh"></i>
          Refresh
        </button>
        <button class="close-btn" @click="handleClose" aria-label="Close">
          <i class="el-icon-close"></i>
        </button>
      </div>
    </div>

    <div class="dialog-body">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-visual">
          <div class="loading-rings">
            <div class="ring ring-1"></div>
            <div class="ring ring-2"></div>
            <div class="ring ring-3"></div>
            <div class="ring-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
        <div class="loading-content">
          <p class="loading-message">{{ currentLoadingMessage }}</p>
          <p class="loading-hint">This usually takes 30-60 seconds</p>
        </div>
        <div class="loading-steps">
          <div
            v-for="(step, idx) in loadingSteps"
            :key="idx"
            class="step"
            :class="{ 'active': idx === currentStepIndex, 'done': idx < currentStepIndex }"
          >
            <span class="step-dot"></span>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          <i class="el-icon-warning-outline"></i>
        </div>
        <p class="error-message">{{ error }}</p>
        <button class="retry-btn" @click="$emit('retry')">
          <i class="el-icon-refresh"></i>
          Try Again
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="!hasSuggestions" class="empty-state">
        <div class="empty-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3 class="empty-title">All Caught Up</h3>
        <p class="empty-desc">No clear patterns found in your unmatched focus items. Your team's work might already be well-aligned with existing goals.</p>
      </div>

      <!-- Results -->
      <div v-else class="results-container">
        <!-- Auto-Applied Summary -->
        <div v-if="autoAppliedAssignments.length > 0" class="auto-applied-banner">
          <div class="banner-icon">
            <i class="el-icon-circle-check"></i>
          </div>
          <div class="banner-content">
            <span class="banner-count">{{ autoAppliedAssignments.length }}</span>
            <span class="banner-text"> focus items automatically matched to existing goals</span>
          </div>
          <button
            class="banner-toggle"
            @click="showAutoAppliedDetails = !showAutoAppliedDetails"
          >
            {{ showAutoAppliedDetails ? 'Hide' : 'Show' }}
            <i :class="showAutoAppliedDetails ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </button>
        </div>

        <!-- Auto-Applied Details (Collapsible) -->
        <transition name="slide-fade">
          <div v-if="showAutoAppliedDetails && autoAppliedAssignments.length > 0" class="auto-applied-details">
            <div
              v-for="(item, idx) in autoAppliedAssignments"
              :key="`auto-${idx}`"
              class="auto-item"
            >
              <span class="auto-item-focus">{{ item.itemText }}</span>
              <span class="auto-item-arrow">→</span>
              <span class="auto-item-goal">{{ item.suggestedGoalTitle }}</span>
            </div>
          </div>
        </transition>

        <!-- Low Confidence Reviews -->
        <div v-if="lowConfidenceAssignments.length > 0" class="section">
          <div class="section-header">
            <h3 class="section-title">Review Matches</h3>
            <span class="section-count">{{ lowConfidenceAssignments.length }}</span>
          </div>
          <p class="section-desc">These matches need your confirmation</p>

          <div class="cards-list">
            <div
              v-for="(suggestion, index) in lowConfidenceAssignments"
              :key="`review-${index}`"
              class="review-card"
              :class="{
                'accepted': acceptedAssignments.includes(index),
                'skipped': skippedAssignments.includes(index)
              }"
            >
              <div class="card-main">
                <div class="focus-item-row">
                  <span class="label">Focus Item</span>
                  <span class="value">{{ suggestion.itemText }}</span>
                  <span v-if="suggestion.item_ids && suggestion.item_ids.length > 1" class="count-badge">
                    {{ suggestion.item_ids.length }}
                  </span>
                </div>
                <div class="suggestion-row">
                  <span class="arrow-icon">↓</span>
                  <div class="goal-suggestion">
                    <span class="label">Suggested Goal</span>
                    <span class="goal-name">{{ suggestion.suggestedGoalTitle }}</span>
                    <span v-if="suggestion.teamName" class="team-name">{{ suggestion.teamName }}</span>
                  </div>
                </div>
                <p v-if="suggestion.reasoning" class="reasoning">{{ suggestion.reasoning }}</p>
              </div>

              <div class="card-actions" v-if="!acceptedAssignments.includes(index) && !skippedAssignments.includes(index)">
                <button
                  class="btn btn-primary"
                  @click="acceptAssignment(index)"
                  :disabled="processingAssignment === index"
                >
                  <i v-if="processingAssignment === index" class="el-icon-loading"></i>
                  <i v-else class="el-icon-check"></i>
                  Accept
                </button>
                <button class="btn btn-secondary" @click="skipAssignment(index)">
                  Skip
                </button>
              </div>
              <div class="card-status" v-else>
                <span v-if="acceptedAssignments.includes(index)" class="status-accepted">
                  <i class="el-icon-circle-check"></i> Matched
                </span>
                <span v-else class="status-skipped">Skipped</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Suggested New Goals -->
        <div v-if="emergentGoals.length > 0" class="section">
          <div class="section-header">
            <h3 class="section-title">Suggested New Goals</h3>
            <span class="section-count">{{ emergentGoals.length }}</span>
          </div>
          <p class="section-desc">New goals discovered from patterns in unmatched work</p>

          <div class="cards-list">
            <div
              v-for="(goal, index) in emergentGoals"
              :key="`goal-${index}`"
              class="goal-card"
              :class="{
                'accepted': acceptedGoals.includes(index),
                'skipped': skippedGoals.includes(index)
              }"
            >
              <div class="card-main">
                <div class="goal-header">
                  <h4 class="goal-title">{{ getGoalTitle(goal) }}</h4>
                  <div class="header-badges">
                    <span
                      v-if="goal.suggestedTeamName"
                      class="team-badge"
                      :title="`Suggested for ${goal.suggestedTeamName} team`"
                    >
                      <i class="el-icon-office-building"></i>
                      {{ goal.suggestedTeamName }}
                    </span>
                    <span
                      class="priority-badge"
                      :class="`priority-${goal.priority}`"
                    >
                      {{ goal.priority }}
                    </span>
                  </div>
                </div>
                <p v-if="goal.suggestedDescription && goal.suggestedTitle" class="goal-desc">{{ goal.suggestedDescription }}</p>

                <div class="focus-count-badge">
                  <i class="el-icon-document"></i>
                  <span>{{ goal.focusItemCount }} focus item{{ goal.focusItemCount !== 1 ? 's' : '' }}</span>
                  <span v-if="goal.focusItemPercentage > 0" class="focus-percentage">({{ goal.focusItemPercentage }}% of team's focus)</span>
                </div>

                <div v-if="goal.relatedItems && goal.relatedItems.length > 0" class="related-items">
                  <span
                    v-for="(item, i) in goal.relatedItems.slice(0, 3)"
                    :key="i"
                    class="related-tag"
                  >
                    {{ item.itemText }}
                  </span>
                  <span v-if="goal.relatedItems.length > 3" class="related-more">
                    +{{ goal.relatedItems.length - 3 }}
                  </span>
                </div>

                <p v-if="goal.reasoning" class="reasoning">{{ goal.reasoning }}</p>

                <!-- Team Selection and Parent Goal Linking -->
                <div v-if="!acceptedGoals.includes(index) && !skippedGoals.includes(index) && goalConfigs[index]" class="goal-config">
                  <!-- Team Selection -->
                  <div class="config-row">
                    <label class="config-label">
                      <i class="el-icon-office-building"></i>
                      Team
                      <span v-if="goalConfigs[index].selectedTeamId === goal.suggestedTeamId" class="recommended-badge">Recommended</span>
                    </label>
                    <el-select
                      v-model="goalConfigs[index].selectedTeamId"
                      size="small"
                      placeholder="Select team"
                      class="team-select"
                      filterable
                    >
                      <el-option
                        v-for="team in availableTeams"
                        :key="team.key"
                        :label="team.key === goal.suggestedTeamId ? team.name + ' (Recommended)' : team.name"
                        :value="team.key"
                      >
                        <span class="team-option">
                          <span class="team-dot" :style="{ background: team.color || '#747474' }"></span>
                          {{ team.name }}
                          <span v-if="team.key === goal.suggestedTeamId" class="team-recommended">(Recommended)</span>
                        </span>
                      </el-option>
                    </el-select>
                  </div>

                  <!-- Parent Goal Linking -->
                  <div class="config-row">
                    <label class="config-label">
                      <i class="el-icon-connection"></i>
                      Link to Parent Goal
                      <span class="optional-badge">Optional</span>
                    </label>
                    <el-select
                      v-model="goalConfigs[index].parentGoalIds"
                      size="small"
                      placeholder="Select parent goal(s)"
                      class="parent-select"
                      multiple
                      filterable
                      clearable
                      :disabled="getAncestorGoals(goalConfigs[index].selectedTeamId).length === 0"
                    >
                      <el-option-group
                        v-for="teamGroup in getAncestorGoals(goalConfigs[index].selectedTeamId)"
                        :key="teamGroup.team.key"
                        :label="teamGroup.team.name"
                      >
                        <el-option
                          v-for="parentGoal in teamGroup.goals"
                          :key="parentGoal.goal_id"
                          :label="parentGoal.quick_preview"
                          :value="parentGoal.goal_id"
                        >
                          <span class="parent-goal-option">
                            <span class="parent-dot" :style="{ background: teamGroup.team.color || '#747474' }"></span>
                            {{ parentGoal.quick_preview }}
                          </span>
                        </el-option>
                      </el-option-group>
                    </el-select>
                    <p v-if="getAncestorGoals(goalConfigs[index].selectedTeamId).length === 0" class="no-parents-hint">
                      No ancestor team goals available
                    </p>
                  </div>
                </div>
              </div>

              <div class="card-actions" v-if="!acceptedGoals.includes(index) && !skippedGoals.includes(index)">
                <button
                  class="btn btn-success"
                  @click="acceptGoal(index)"
                  :disabled="processingGoal === index || !goalConfigs[index] || !goalConfigs[index].selectedTeamId"
                >
                  <i v-if="processingGoal === index" class="el-icon-loading"></i>
                  <i v-else class="el-icon-plus"></i>
                  Create Goal
                </button>
                <button class="btn btn-secondary" @click="skipGoal(index)">
                  Skip
                </button>
              </div>
              <div class="card-status" v-else>
                <span v-if="acceptedGoals.includes(index)" class="status-accepted">
                  <i class="el-icon-circle-check"></i> Created
                </span>
                <span v-else class="status-skipped">Skipped</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div slot="footer" class="dialog-footer">
      <div class="footer-stats">
        <span v-if="autoAppliedAssignments.length > 0" class="stat">
          <i class="el-icon-circle-check"></i>
          {{ autoAppliedAssignments.length }} auto-matched
        </span>
        <span v-if="totalAccepted > 0" class="stat stat-accepted">
          {{ totalAccepted }} accepted
        </span>
        <span v-if="totalSkipped > 0" class="stat stat-skipped">
          {{ totalSkipped }} skipped
        </span>
      </div>
      <button class="btn btn-close" @click="handleClose">
        {{ allProcessed ? 'Done' : 'Close' }}
      </button>
    </div>
  </el-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'EmergentGoalsModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    result: {
      type: Object,
      default: null,
    },
    teamId: {
      type: String,
      required: true,
    },
    teamName: {
      type: String,
      default: '',
    },
    unmatchedFocusItems: {
      type: Array,
      default: () => [],
    },
    totalFocusItems: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      acceptedAssignments: [],
      skippedAssignments: [],
      acceptedGoals: [],
      skippedGoals: [],
      processingAssignment: null,
      processingGoal: null,
      autoAppliedAssignments: [],
      autoApplyComplete: false,
      showAutoAppliedDetails: false,
      // Goal configuration (team selection, parent linking)
      goalConfigs: [],
      // Loading state
      currentStepIndex: 0,
      loadingSteps: [
        'Scanning focus items...',
        'Finding patterns...',
        'Matching to goals...',
        'Generating suggestions...',
      ],
      loadingInterval: null,
    };
  },
  computed: {
    localVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },
    currentLoadingMessage() {
      return this.loadingSteps[this.currentStepIndex] || 'Analyzing...';
    },
    allSuggestedAssignments() {
      return (this.result && this.result.suggestedAssignments) || [];
    },
    rawEmergentGoals() {
      return (this.result && this.result.emergentGoals) || [];
    },
    // Get all item_ids that belong to emergent (new) goals
    // These should NOT be auto-matched - they wait for the new goal to be created
    emergentGoalItemIds() {
      const ids = new Set();
      this.rawEmergentGoals.forEach(goal => {
        (goal.relatedItems || []).forEach(item => {
          (item.item_ids || []).forEach(id => ids.add(id));
        });
      });
      return ids;
    },
    // High confidence assignments to EXISTING goals only
    // Excludes items that belong to emergent goals
    highConfidenceAssignments() {
      return this.allSuggestedAssignments.filter(a => {
        if (a.confidence !== 'high' && a.confidence !== 'medium') {
          return false;
        }
        // Exclude if ANY of this assignment's items belong to an emergent goal
        const itemIds = a.item_ids || [];
        const belongsToEmergentGoal = itemIds.some(id => this.emergentGoalItemIds.has(id));
        return !belongsToEmergentGoal;
      });
    },
    lowConfidenceAssignments() {
      return this.allSuggestedAssignments.filter(a => a.confidence === 'low');
    },
    autoMatchedItemIds() {
      const ids = new Set();
      this.highConfidenceAssignments.forEach(a => {
        (a.item_ids || []).forEach(id => ids.add(id));
      });
      return ids;
    },
    // Emergent goals with their related items and team attribution
    // Only show goals with more than 5 focus items AND at least 5% of team's focus (meaningful patterns)
    emergentGoals() {
      const MIN_FOCUS_ITEMS = 5; // Minimum focus items required for a suggested goal
      const MIN_FOCUS_PERCENTAGE = 5; // Minimum % of team's focus required
      return this.rawEmergentGoals.map(goal => {
        const relatedItems = goal.relatedItems || [];
        const focusItemCount = relatedItems.reduce((sum, item) => sum + ((item.item_ids && item.item_ids.length) || 1), 0);

        // Calculate percentage of total focus items
        const focusItemPercentage = this.totalFocusItems > 0
          ? Math.round((focusItemCount / this.totalFocusItems) * 100)
          : 0;

        // Calculate suggested team based on focus item distribution
        const suggestedTeam = this.determineSuggestedTeam(relatedItems);

        return {
          ...goal,
          relatedItems: relatedItems,
          focusItemCount,
          focusItemPercentage,
          suggestedTeamId: suggestedTeam.teamId,
          suggestedTeamName: suggestedTeam.teamName,
        };
      }).filter(goal => goal.relatedItems.length > 0 && goal.focusItemCount > MIN_FOCUS_ITEMS && goal.focusItemPercentage >= MIN_FOCUS_PERCENTAGE);
    },
    error() {
      return (this.result && this.result.error) || null;
    },
    availableTeams() {
      return this.$store.getters['team/teamsall'] || [];
    },
    ancestorGoalsForTeam() {
      return this.$store.getters['goals/ancestorGoalsForTeam'];
    },
    hasSuggestions() {
      return this.lowConfidenceAssignments.length > 0 ||
             this.emergentGoals.length > 0 ||
             this.autoAppliedAssignments.length > 0;
    },
    totalAccepted() {
      return this.acceptedAssignments.length + this.acceptedGoals.length;
    },
    totalSkipped() {
      return this.skippedAssignments.length + this.skippedGoals.length;
    },
    allProcessed() {
      const totalLowConfidence = this.lowConfidenceAssignments.length;
      const totalGoals = this.emergentGoals.length;
      const processedAssignments = this.acceptedAssignments.length + this.skippedAssignments.length;
      const processedGoals = this.acceptedGoals.length + this.skippedGoals.length;
      return processedAssignments >= totalLowConfidence && processedGoals >= totalGoals;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetState();
        this.startLoadingAnimation();
      } else {
        this.stopLoadingAnimation();
      }
    },
    loading(newVal) {
      if (!newVal) {
        this.stopLoadingAnimation();
      }
    },
    result: {
      immediate: true,
      async handler(newResult) {
        if (newResult && !this.autoApplyComplete && this.visible) {
          await this.autoApplyHighConfidenceMatches();
        }
      },
    },
    emergentGoals: {
      immediate: true,
      handler(newGoals) {
        if (newGoals && newGoals.length > 0 && this.goalConfigs.length !== newGoals.length) {
          this.initializeGoalConfigs();
        }
      },
    },
  },
  beforeDestroy() {
    this.stopLoadingAnimation();
  },
  methods: {
    ...mapActions('focus', ['updateFocusItemGoalIds', 'markAutoApplyDone', 'invalidateEmergentGoalsCache']),
    ...mapActions('goals', ['saveStructuredGoals']),

    handleRefresh() {
      // Invalidate cache and trigger fresh AI analysis
      this.invalidateEmergentGoalsCache({ teamId: this.teamId }).then(() => {
        this.resetState();
        this.$emit('retry');
      });
    },

    resetState() {
      this.acceptedAssignments = [];
      this.skippedAssignments = [];
      this.acceptedGoals = [];
      this.skippedGoals = [];
      this.processingAssignment = null;
      this.processingGoal = null;
      this.autoAppliedAssignments = [];
      this.autoApplyComplete = false;
      this.showAutoAppliedDetails = false;
      this.goalConfigs = [];
      this.currentStepIndex = 0;
    },
    initializeGoalConfigs() {
      // Initialize config for each emergent goal
      // Use AI-suggested team and parent goals if available, otherwise use defaults
      this.goalConfigs = this.emergentGoals.map((goal) => {
        // Check if AI suggested a team (by ID or name)
        let selectedTeamId = this.teamId; // Default to current team
        if (goal.suggestedTeamId) {
          // AI suggested a team ID directly
          const teamExists = this.availableTeams.some(t => t.key === goal.suggestedTeamId);
          if (teamExists) {
            selectedTeamId = goal.suggestedTeamId;
          }
        } else if (goal.suggestedTeamName) {
          // AI suggested a team name - find matching team
          const matchedTeam = this.availableTeams.find(
            t => t.name && t.name.toLowerCase() === goal.suggestedTeamName.toLowerCase()
          );
          if (matchedTeam) {
            selectedTeamId = matchedTeam.key;
          }
        }

        // Use AI-suggested parent goals if available
        const parentGoalIds = Array.isArray(goal.suggestedParentGoalIds)
          ? goal.suggestedParentGoalIds
          : [];

        return {
          selectedTeamId,
          parentGoalIds,
        };
      });
    },
    getAncestorGoals(teamId) {
      if (!teamId || !this.ancestorGoalsForTeam) return [];
      return this.ancestorGoalsForTeam(teamId) || [];
    },
    getGoalTitle(goal) {
      // If title exists and is different from description, use it
      if (goal.suggestedTitle && goal.suggestedTitle.trim()) {
        return goal.suggestedTitle;
      }
      // Generate title from description if no title
      if (goal.suggestedDescription) {
        const desc = goal.suggestedDescription.trim();
        // Take first sentence or first 50 chars
        const firstSentence = desc.split(/[.!?]/)[0];
        if (firstSentence.length <= 60) {
          return firstSentence;
        }
        // Truncate at word boundary
        return desc.substring(0, 57).replace(/\s+\S*$/, '') + '...';
      }
      return 'Untitled Goal';
    },
    /**
     * Determine suggested team for an emergent goal based on focus item distribution
     * - If items come from one team → recommend that team
     * - If items come from sibling teams → recommend their common parent
     * @param relatedItems - Array of related items from the emergent goal
     * @returns Object with teamId and teamName of the recommended team
     */
    determineSuggestedTeam(relatedItems) {
      const teamCounts = new Map();

      for (const item of relatedItems) {
        // Find original unmatched item to get team info
        const original = this.unmatchedFocusItems.find(u =>
          u.itemText.toLowerCase().trim() === item.itemText.toLowerCase().trim()
        );

        if (original && original.people) {
          for (const person of original.people) {
            if (person.team_id) {
              const current = teamCounts.get(person.team_id) || {
                count: 0,
                teamName: person.team_name,
                teamId: person.team_id,
              };
              current.count++;
              teamCounts.set(person.team_id, current);
            }
          }
        }
      }

      // Get list of teams that contributed
      const contributingTeams = Array.from(teamCounts.keys());

      // Early return if no teams found
      if (contributingTeams.length === 0) {
        return { teamId: null, teamName: null };
      }

      // If only one team, return it
      if (contributingTeams.length === 1) {
        const teamId = contributingTeams[0];
        const data = teamCounts.get(teamId);
        return { teamId: data.teamId, teamName: data.teamName };
      }

      // Calculate total people across all teams
      let totalPeople = 0;
      for (const [, data] of teamCounts) {
        totalPeople += data.count;
      }

      // Check if one team has dominant share (≥70%)
      const DOMINANCE_THRESHOLD = 0.7;
      let dominantTeam = null;
      for (const [teamId, data] of teamCounts) {
        const share = data.count / totalPeople;
        if (share >= DOMINANCE_THRESHOLD) {
          dominantTeam = { teamId, teamName: data.teamName };
        }
      }

      if (dominantTeam) {
        return { teamId: dominantTeam.teamId, teamName: dominantTeam.teamName };
      }

      // No dominant team - find the Lowest Common Ancestor (LCA)
      // Helper to get ancestor chain for a team (from team up to root)
      const getAncestorChain = (teamId) => {
        const chain = [teamId];
        let currentId = teamId;
        const visited = new Set();

        while (currentId && !visited.has(currentId)) {
          visited.add(currentId);
          const team = this.availableTeams.find(t => t.key === currentId);
          if (team && team.parent) {
            chain.push(team.parent);
            currentId = team.parent;
          } else {
            break;
          }
        }
        return chain;
      };

      // Get ancestor chains for all contributing teams
      const ancestorChains = contributingTeams.map(teamId => getAncestorChain(teamId));

      // Find LCA by checking which ancestor is common to all chains
      // Start from the first team's ancestors and find the first one that appears in all chains
      const firstChain = ancestorChains[0] || [];
      let lcaId = null;

      for (const ancestorId of firstChain) {
        const isCommon = ancestorChains.every(chain => chain.includes(ancestorId));
        if (isCommon) {
          lcaId = ancestorId;
          break;
        }
      }

      if (lcaId) {
        const lcaTeam = this.availableTeams.find(t => t.key === lcaId);
        if (lcaTeam) {
          return { teamId: lcaId, teamName: lcaTeam.name };
        }
      }

      // Fallback to team with most focus items if LCA not found
      let maxTeam = { teamId: null, teamName: null };
      let maxCount = 0;
      for (const [, data] of teamCounts) {
        if (data.count > maxCount) {
          maxCount = data.count;
          maxTeam = { teamId: data.teamId, teamName: data.teamName };
        }
      }

      return maxTeam;
    },
    startLoadingAnimation() {
      this.currentStepIndex = 0;
      this.loadingInterval = setInterval(() => {
        this.currentStepIndex = (this.currentStepIndex + 1) % this.loadingSteps.length;
      }, 3000);
    },
    stopLoadingAnimation() {
      if (this.loadingInterval) {
        clearInterval(this.loadingInterval);
        this.loadingInterval = null;
      }
    },
    async autoApplyHighConfidenceMatches() {
      const toApply = this.highConfidenceAssignments;
      if (toApply.length === 0) {
        this.autoApplyComplete = true;
        return;
      }

      // Always attempt to apply matches, even if loaded from cache
      // The update function is idempotent - it only updates items without a goal_id
      // This ensures items get matched even if a previous auto-apply was partial
      const wasFromCache = this.result && this.result.autoApplyDone;

      try {
        // Include itemText for text-based matching fallback
        // This ensures items are matched even if item_ids differ between historical and current data
        const updates = toApply.map(suggestion => ({
          item_ids: suggestion.item_ids || [],
          goal_id: suggestion.suggestedGoalId,
          itemText: suggestion.itemText || '', // For text-based matching fallback
        }));

        const result = await this.updateFocusItemGoalIds(updates);

        if (result && result.success) {
          this.autoAppliedAssignments = toApply.map((a, idx) => ({
            ...a,
            originalIndex: idx,
          }));

          // Mark auto-apply as done in the cache (if we have a suggestionId and not already done)
          if (this.result && this.result.suggestionId && !wasFromCache) {
            await this.markAutoApplyDone({ suggestionId: this.result.suggestionId });
          }

          // Emit event to refresh the main view (only if items were actually updated)
          if (result.updatedCount > 0) {
            this.$emit('assignments-auto-applied', {
              count: toApply.length,
              assignments: toApply,
            });
          }
        } else {
          console.error('[EmergentGoals] Auto-apply failed:', result);
          this.$message.error('Failed to auto-match some focus items');
        }
      } catch (error) {
        console.error('Error auto-applying matches:', error);
        this.$message.error('Failed to auto-match focus items');
      } finally {
        this.autoApplyComplete = true;
      }
    },
    async acceptAssignment(index) {
      const suggestion = this.lowConfidenceAssignments[index];
      if (!suggestion) return;

      this.processingAssignment = index;
      try {
        await this.updateFocusItemGoalIds([{
          item_ids: suggestion.item_ids || [],
          goal_id: suggestion.suggestedGoalId,
          itemText: suggestion.itemText || '', // For text-based matching fallback
        }]);
        this.acceptedAssignments.push(index);
        this.$emit('assignment-accepted', { index, suggestion });
      } catch (error) {
        console.error('Error accepting assignment:', error);
        this.$message.error('Failed to assign focus items to goal');
      } finally {
        this.processingAssignment = null;
      }
    },
    skipAssignment(index) {
      this.skippedAssignments.push(index);
      this.$emit('assignment-skipped', { index });
    },
    async acceptGoal(index) {
      const goal = this.emergentGoals[index];
      const config = this.goalConfigs[index];

      if (!goal || !config) {
        return;
      }

      // Validate team selection
      if (!config.selectedTeamId) {
        this.$message.warning('Please select a team for this goal');
        return;
      }

      this.processingGoal = index;
      try {
        // Use getGoalTitle to handle missing titles
        const goalTitle = this.getGoalTitle(goal);
        const newGoalData = {
          title: goalTitle,
          description: goal.suggestedDescription || goalTitle,
          parent_goal_ids: config.parentGoalIds || [],
        };

        const result = await this.saveStructuredGoals({
          teamId: config.selectedTeamId,
          goals: [newGoalData],
        });

        if (result && result.success && result.newGoals && result.newGoals.length > 0) {
          const newGoalId = result.newGoals[0].goal_id_ai;

          if (newGoalId && goal.relatedItems && goal.relatedItems.length > 0) {
            // Include itemText for each item for text-based matching fallback
            const updates = goal.relatedItems
              .filter(item => (item.item_ids && item.item_ids.length > 0) || item.itemText)
              .map(item => ({
                item_ids: item.item_ids || [],
                goal_id: newGoalId,
                itemText: item.itemText || '', // For text-based matching fallback
              }));
            if (updates.length > 0) {
              await this.updateFocusItemGoalIds(updates);
            }
          }

          this.acceptedGoals.push(index);
          this.$emit('goal-created', { index, goal, teamId: config.selectedTeamId });

          // Find team name for success message
          const selectedTeam = this.availableTeams.find(t => t.key === config.selectedTeamId);
          const teamName = (selectedTeam && selectedTeam.name) ? selectedTeam.name : 'selected team';
          this.$message.success(`Goal created for ${teamName}`);
        } else {
          console.error('[EmergentGoals] Create goal failed:', result);
          throw new Error((result && result.error) || 'Failed to create goal');
        }
      } catch (error) {
        console.error('Error creating goal:', error);
        this.$message.error('Failed to create goal: ' + (error.message || 'Unknown error'));
      } finally {
        this.processingGoal = null;
      }
    },
    skipGoal(index) {
      this.skippedGoals.push(index);
      this.$emit('goal-skipped', { index });
    },
    handleClose() {
      this.$emit('close');
      this.$emit('update:visible', false);
    },
  },
};
</script>

<style lang="scss" scoped>
// Design tokens matching Happily's design system
$primary: #6C30CC;
$primary-light: #8B5FD4;
$primary-bg: #F8F5FC;
$success: #27BC6C;
$success-bg: #E8F8EF;
$warning: #F7B924;
$warning-bg: #FEF9E8;
$danger: #CC3030;
$text-primary: #2B2B2B;
$text-secondary: #6C6C6C;
$text-muted: #9A9A9A;
$border-color: #EAEAEA;
$bg-light: #F9F9F9;

// Dialog structure
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid $border-color;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: $primary;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
  color: $text-primary;
  margin: 0;
  line-height: 1.2;
}

.header-subtitle {
  font-size: 13px;
  color: $text-muted;
  margin: 2px 0 0 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: $text-secondary;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  i {
    font-size: 14px;
  }

  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
    color: $text-primary;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: $bg-light;
    color: $text-primary;
  }
}

.dialog-body {
  padding: 0;
  max-height: 60vh;
  overflow-y: auto;
}

// Loading State
.loading-state {
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.loading-visual {
  position: relative;
  width: 100px;
  height: 100px;
}

.loading-rings {
  position: relative;
  width: 100%;
  height: 100%;
}

.ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid transparent;

  &.ring-1 {
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    border-top-color: rgba($primary, 0.15);
    animation: spin 3s linear infinite;
  }

  &.ring-2 {
    width: 75%;
    height: 75%;
    top: 12.5%;
    left: 12.5%;
    border-right-color: rgba($primary, 0.3);
    animation: spin 2s linear infinite reverse;
  }

  &.ring-3 {
    width: 50%;
    height: 50%;
    top: 25%;
    left: 25%;
    border-bottom-color: $primary;
    animation: spin 1.5s linear infinite;
  }
}

.ring-center {
  position: absolute;
  width: 36px;
  height: 36px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: $primary-bg;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $primary;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-content {
  text-align: center;
}

.loading-message {
  font-size: 15px;
  font-weight: 600;
  color: $text-primary;
  margin: 0 0 6px 0;
  min-height: 22px;
}

.loading-hint {
  font-size: 13px;
  color: $text-muted;
  margin: 0;
}

.loading-steps {
  display: flex;
  gap: 8px;
}

.step {
  .step-dot {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $border-color;
    transition: all 0.3s;
  }

  &.active .step-dot {
    background: $primary;
    transform: scale(1.25);
  }

  &.done .step-dot {
    background: $primary-light;
  }
}

// Error State
.error-state {
  padding: 60px 24px;
  text-align: center;
}

.error-icon {
  font-size: 48px;
  color: $danger;
  margin-bottom: 16px;
}

.error-message {
  font-size: 14px;
  color: $text-secondary;
  margin: 0 0 20px 0;
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border: 1px solid $primary;
  border-radius: 6px;
  background: white;
  color: $primary;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: $primary-bg;
  }
}

// Empty State
.empty-state {
  padding: 60px 24px;
  text-align: center;
}

.empty-icon {
  color: $success;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 16px;
  font-weight: 600;
  color: $text-primary;
  margin: 0 0 8px 0;
}

.empty-desc {
  font-size: 14px;
  color: $text-secondary;
  margin: 0;
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

// Results
.results-container {
  padding: 0;
}

// Auto-applied Banner
.auto-applied-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  background: $success-bg;
  border-bottom: 1px solid rgba($success, 0.2);
}

.banner-icon {
  color: $success;
  font-size: 20px;
}

.banner-content {
  flex: 1;
  font-size: 14px;
  color: $text-secondary;
}

.banner-count {
  font-weight: 700;
  color: $success;
}

.banner-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: $text-muted;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: rgba($success, 0.1);
    color: $text-secondary;
  }
}

.auto-applied-details {
  padding: 12px 24px 16px;
  background: rgba($success-bg, 0.5);
  border-bottom: 1px solid $border-color;
}

.auto-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;

  &:not(:last-child) {
    border-bottom: 1px dashed $border-color;
  }
}

.auto-item-focus {
  color: $text-secondary;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.auto-item-arrow {
  color: $text-muted;
}

.auto-item-goal {
  color: $success;
  font-weight: 500;
}

// Sections
.section {
  padding: 20px 24px;

  &:not(:last-child) {
    border-bottom: 1px solid $border-color;
  }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: $text-primary;
  margin: 0;
}

.section-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: $bg-light;
  color: $text-secondary;
  font-size: 12px;
  font-weight: 600;
}

.section-desc {
  font-size: 13px;
  color: $text-muted;
  margin: 0 0 16px 0;
}

// Cards
.cards-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.review-card,
.goal-card {
  border: 1px solid $border-color;
  border-radius: 8px;
  background: white;
  overflow: hidden;
  transition: all 0.15s;

  &:hover:not(.accepted):not(.skipped) {
    border-color: #d0d0d0;
  }

  &.accepted {
    background: $success-bg;
    border-color: rgba($success, 0.3);
  }

  &.skipped {
    opacity: 0.6;
    background: $bg-light;
  }
}

.card-main {
  padding: 16px;
}

.focus-item-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
}

.label {
  font-size: 11px;
  font-weight: 600;
  color: $text-muted;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.value {
  font-size: 14px;
  color: $text-primary;
  font-weight: 500;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: $primary-bg;
  color: $primary;
  font-size: 11px;
  font-weight: 600;
}

.suggestion-row {
  display: flex;
  gap: 10px;
  padding: 12px;
  margin: 0 -4px;
  background: $bg-light;
  border-radius: 6px;
}

.arrow-icon {
  color: $primary;
  font-weight: bold;
}

.goal-suggestion {
  flex: 1;
}

.goal-name {
  display: block;
  font-size: 14px;
  color: $text-primary;
  font-weight: 500;
  margin-top: 2px;
}

.team-name {
  display: block;
  font-size: 12px;
  color: $text-muted;
  margin-top: 2px;
}

.reasoning {
  font-size: 13px;
  color: $text-secondary;
  font-style: italic;
  margin: 12px 0 0 0;
  line-height: 1.5;
}

// Goal Card specifics
.goal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.goal-title {
  font-size: 15px;
  font-weight: 600;
  color: $text-primary;
  margin: 0;
  line-height: 1.3;
}

.header-badges {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.team-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: $primary-bg;
  color: $primary;

  i {
    font-size: 10px;
  }
}

.priority-badge {
  flex-shrink: 0;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;

  &.priority-high {
    background: rgba($danger, 0.1);
    color: $danger;
  }

  &.priority-medium {
    background: $warning-bg;
    color: darken($warning, 15%);
  }

  &.priority-low {
    background: $bg-light;
    color: $text-muted;
  }
}

.goal-desc {
  font-size: 14px;
  color: $text-secondary;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.focus-count-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: $primary-bg;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  color: $primary;
  margin-bottom: 12px;

  i {
    font-size: 14px;
  }

  .focus-percentage {
    color: $text-secondary;
    font-weight: 400;
  }
}

.related-items {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.related-tag {
  padding: 4px 10px;
  background: $bg-light;
  border-radius: 4px;
  font-size: 12px;
  color: $text-secondary;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.related-more {
  padding: 4px 10px;
  color: $text-muted;
  font-size: 12px;
}

// Goal Configuration (Team & Parent Goal Selection)
.goal-config {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed $border-color;
}

.config-row {
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

.config-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: $text-secondary;
  margin-bottom: 6px;

  i {
    font-size: 13px;
    color: $text-muted;
  }
}

.optional-badge {
  font-size: 10px;
  font-weight: 500;
  color: $text-muted;
  background: $bg-light;
  padding: 2px 6px;
  border-radius: 3px;
  margin-left: 4px;
}

.recommended-badge {
  font-size: 10px;
  font-weight: 600;
  color: $success;
  background: $success-bg;
  padding: 2px 6px;
  border-radius: 3px;
  margin-left: 4px;
}

.team-recommended {
  font-size: 11px;
  color: $success;
  margin-left: 4px;
}

.team-select,
.parent-select {
  width: 100%;
}

.team-option,
.parent-goal-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.team-dot,
.parent-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.no-parents-hint {
  font-size: 12px;
  color: $text-muted;
  font-style: italic;
  margin: 4px 0 0 0;
}

// Card Actions
.card-actions {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid $border-color;
  background: $bg-light;
}

.card-status {
  padding: 12px 16px;
  border-top: 1px solid $border-color;
  background: $bg-light;
  font-size: 13px;
}

.status-accepted {
  color: $success;
  font-weight: 500;

  i {
    margin-right: 4px;
  }
}

.status-skipped {
  color: $text-muted;
}

// Buttons
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-primary {
  background: $primary;
  color: white;

  &:hover:not(:disabled) {
    background: darken($primary, 8%);
  }
}

.btn-success {
  background: $success;
  color: white;

  &:hover:not(:disabled) {
    background: darken($success, 8%);
  }
}

.btn-secondary {
  background: white;
  color: $text-secondary;
  border: 1px solid $border-color;

  &:hover:not(:disabled) {
    background: $bg-light;
    border-color: #d0d0d0;
  }
}

.btn-close {
  background: white;
  color: $text-primary;
  border: 1px solid $border-color;
  min-width: 90px;

  &:hover {
    background: $bg-light;
  }
}

// Footer
.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid $border-color;
  background: $bg-light;
}

.footer-stats {
  display: flex;
  gap: 16px;
}

.stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: $success;

  i {
    font-size: 14px;
  }
}

.stat-accepted {
  color: $primary;
}

.stat-skipped {
  color: $text-muted;
}

// Transitions
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>

<style lang="scss">
// Global dialog overrides
.emergent-goals-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    padding: 0;
    margin: 0;
  }

  .el-dialog__body {
    padding: 0;
  }

  .el-dialog__footer {
    padding: 0;
  }
}
</style>
